/**
 * Machine Salt - Browser fingerprinting for device-bound encryption
 *
 * Uses stable browser features to create a unique machine identifier.
 * This ensures encrypted data can only be decrypted on the same machine.
 */

/**
 * Get Canvas fingerprint
 * Uses GPU rendering differences to create unique fingerprint
 */
function getCanvasFingerprint(): string {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Canvas 2D context not available');
  }

  canvas.width = 200;
  canvas.height = 50;

  // Draw background
  ctx.fillStyle = '#f0f0f0';
  ctx.fillRect(0, 0, 200, 50);

  // Draw text with emoji (rendering varies by system)
  ctx.fillStyle = '#069';
  ctx.font = '16px Arial';
  ctx.fillText('QuickClip v1.0', 10, 30);

  // Draw gradient rectangle
  const gradient = ctx.createLinearGradient(0, 0, 200, 50);
  gradient.addColorStop(0, 'rgba(255, 0, 0, 0.5)');
  gradient.addColorStop(0.5, 'rgba(0, 255, 0, 0.5)');
  gradient.addColorStop(1, 'rgba(0, 0, 255, 0.5)');
  ctx.fillStyle = gradient;
  ctx.fillRect(10, 10, 30, 30);

  // Draw some curves
  ctx.beginPath();
  ctx.moveTo(50, 25);
  ctx.quadraticCurveTo(100, 0, 150, 25);
  ctx.quadraticCurveTo(100, 50, 50, 25);
  ctx.fillStyle = 'rgba(255, 255, 0, 0.5)';
  ctx.fill();

  return canvas.toDataURL();
}

/**
 * Get WebGL vendor and renderer info
 * Very stable across sessions, unique per GPU/driver
 */
function getWebGLInfo(): { vendor: string; renderer: string } {
  const canvas = document.createElement('canvas');
  const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;

  if (!gl) {
    return { vendor: 'unknown', renderer: 'unknown' };
  }

  const debugInfo = gl.getExtension('WEBGL_debug_renderer_info') as { UNMASKED_VENDOR_WEBGL: number; UNMASKED_RENDERER_WEBGL: number } | null;
  if (!debugInfo) {
    // Privacy mode or extension not available
    return { vendor: 'unavailable', renderer: 'unavailable' };
  }

  const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || 'unknown';
  const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || 'unknown';

  return { vendor, renderer };
}

/**
 * Get hardware information
 */
function getHardwareInfo(): string {
  const concurrency = navigator.hardwareConcurrency || 'unknown';
  const deviceMemory = (navigator as { deviceMemory?: number }).deviceMemory || 'unknown';
  const maxTouchPoints = navigator.maxTouchPoints || 0;

  return `${concurrency}:${deviceMemory}:${maxTouchPoints}`;
}

/**
 * Get screen information
 */
function getScreenInfo(): string {
  const width = screen.width;
  const height = screen.height;
  const colorDepth = screen.colorDepth;
  const pixelRatio = window.devicePixelRatio || 1;

  return `${width}x${height}x${colorDepth}x${pixelRatio}`;
}

/**
 * Get browser language and timezone
 */
function getLocaleInfo(): string {
  const language = navigator.language || 'unknown';
  const languages = navigator.languages ? navigator.languages.join(',') : language;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown';

  return `${language}:${languages}:${timezone}`;
}

/**
 * Get machine salt - combines multiple stable browser features
 *
 * Features used (in order of importance):
 * 1. Canvas fingerprint - GPU rendering differences
 * 2. WebGL vendor/renderer - GPU/driver info
 * 3. Hardware info - CPU cores, memory
 * 4. Screen info - resolution, color depth
 * 5. Locale info - language, timezone
 *
 * @returns A string unique to this machine/browser combination
 */
export function getMachineSalt(): string {
  const parts: string[] = [];

  // 1. Canvas fingerprint (most unique)
  try {
    parts.push(getCanvasFingerprint());
  } catch {
    parts.push('canvas-error');
  }

  // 2. WebGL info (very stable)
  try {
    const webgl = getWebGLInfo();
    parts.push(webgl.vendor, webgl.renderer);
  } catch {
    parts.push('webgl-error', 'webgl-error');
  }

  // 3. Hardware info (stable)
  try {
    parts.push(getHardwareInfo());
  } catch {
    parts.push('hw-error');
  }

  // 4. Screen info (very stable)
  try {
    parts.push(getScreenInfo());
  } catch {
    parts.push('screen-error');
  }

  // 5. Locale info (less unique but stable)
  try {
    parts.push(getLocaleInfo());
  } catch {
    parts.push('locale-error');
  }

  return parts.join('|');
}

/**
 * Check if machine salt is available
 * Some browsers in privacy mode may disable some features
 */
export function isMachineSaltAvailable(): boolean {
  try {
    const salt = getMachineSalt();
    return salt.length > 100 && !salt.includes('error');
  } catch {
    return false;
  }
}
